import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformSDKFloatToRESTFloat } from '@wix/sdk-runtime/transformations/float';
import { transformSDKTimestampToRESTTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformSDKFieldMaskToRESTFieldMask } from '@wix/sdk-runtime/transformations/field-mask';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

const TRANSFORM_PATHS_0 = [
  { path: 'marketingConsent.createdDate' },
  { path: 'marketingConsent.updatedDate' },
  { path: 'marketingConsent.lastConfirmationActivity.updatedDate' },
  { path: 'marketingConsent.lastRevokeActivity.updatedDate' },
];
const TRANSFORM_PATHS_1 = [{ path: 'mask' }];
const TRANSFORM_PATHS_2 = [{ path: 'lastRevokeActivity.updatedDate' }];
const TRANSFORM_PATHS_3 = [
  { path: 'query.sort.origin.latitude' },
  { path: 'query.sort.origin.longitude' },
];
const TRANSFORM_PATHS_4 = [
  { path: 'info.createdDate' },
  { path: 'info.updatedDate' },
  { path: 'info.lastConfirmationActivity.updatedDate' },
  { path: 'info.lastRevokeActivity.updatedDate' },
];
const TRANSFORM_PATHS_5 = [
  { path: 'results.marketingConsent.createdDate' },
  { path: 'results.marketingConsent.updatedDate' },
  { path: 'results.marketingConsent.lastConfirmationActivity.updatedDate' },
  { path: 'results.marketingConsent.lastRevokeActivity.updatedDate' },
];

function resolveWixMarketingV1MarketingConsentServiceUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'www.wixapis.com': [
      {
        srcPath: '/marketing-consent',
        destPath: '',
      },
    ],
    'manage._base_domain_': [
      {
        srcPath: '/_api/marketing/v1',
        destPath: '/v1',
      },
    ],
    _: [
      {
        srcPath: '/_api/marketing-consent/v1/marketing-consent',
        destPath: '/v1/marketing-consent',
      },
      {
        srcPath: '/_api/marketing-consent/v1/bulk',
        destPath: '/v1/bulk',
      },
      {
        srcPath: '/marketing/marketing-consent/v1/bulk/marketing-consent',
        destPath: '/v1/bulk/marketing-consent',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/marketing/marketing-consent/v1/bulk/marketing-consent',
        destPath: '/v1/bulk/marketing-consent',
      },
      {
        srcPath: '/marketing/marketing-consent/v1/marketing-consent',
        destPath: '/v1/marketing-consent',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_marketing_marketing-consent';

/** Retrieves a marketing consent. */
export function getMarketingConsent(
  payload: object
): RequestOptionsFactory<any> {
  function __getMarketingConsent({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.marketing.marketing_consent.v1.marketing_consent',
      method: 'GET' as any,
      methodFqn: 'wix.marketing.v1.MarketingConsentService.GetMarketingConsent',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixMarketingV1MarketingConsentServiceUrl({
        protoPath: '/v1/marketing-consent/{marketingConsentId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: TRANSFORM_PATHS_0,
          },
        ]),
    };

    return metadata;
  }

  return __getMarketingConsent;
}

/**
 * Retrieves a marketing consent by its details.
 * Required fields:
 * - `details.type`.
 * - `details.email` OR `details.phone`.
 *
 * >**Note:** Due to the ongoing development of our new documentation portal, the query parameter is not displaying as expected. Use the `details` object located in the marketing consent object in the request. You can also see the code example for reference.
 */
export function getMarketingConsentByIdentifier(
  payload: object
): RequestOptionsFactory<any> {
  function __getMarketingConsentByIdentifier({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.marketing.marketing_consent.v1.marketing_consent',
      method: 'GET' as any,
      methodFqn:
        'wix.marketing.v1.MarketingConsentService.GetMarketingConsentByIdentifier',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixMarketingV1MarketingConsentServiceUrl({
        protoPath: '/v1/marketing-consent/get-by',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: TRANSFORM_PATHS_0,
          },
        ]),
    };

    return metadata;
  }

  return __getMarketingConsentByIdentifier;
}

/**
 * Creates a confirmed marketing consent with a `state` of `CONFIRMED`.
 * Required fields:
 * - `details.type`.
 * - `details.email` OR `details.phone`.
 * - `lastConfirmationActivity`.
 *
 * To create a marketing consent with a different state, use Upsert Marketing Consent, or Bulk Upsert Marketing Consent.
 */
export function createMarketingConsent(
  payload: object
): RequestOptionsFactory<any> {
  function __createMarketingConsent({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: TRANSFORM_PATHS_0,
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.marketing.marketing_consent.v1.marketing_consent',
      method: 'POST' as any,
      methodFqn:
        'wix.marketing.v1.MarketingConsentService.CreateMarketingConsent',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixMarketingV1MarketingConsentServiceUrl({
        protoPath: '/v1/marketing-consent',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: TRANSFORM_PATHS_0,
          },
        ]),
    };

    return metadata;
  }

  return __createMarketingConsent;
}

/**
 * Updates a marketing consent.
 * Required fields:
 * - `details.type`.
 * - `details.email` OR `details.phone`.
 * - `state`.
 *
 * When a marketing consent's `state` is `PENDING` or `CONFIRMED`, the `info.lastConfirmationActivity` field is required.
 * When a marketing consent's `state` is `REVOKED`, the `info.lastRevokeActivity` field is required.
 *
 * >**Note:** For existing marketing consents with `{"type": "EMAIL"}`, you can't update the `state` to `UNKNOWN_STATE`. Trying to do so maintains the current state. However, you can create a new marketing consent and set the `state` to `UNKNOWN_STATE`. Note that you can't create more than a single consent per email or phone number.
 */
export function updateMarketingConsent(
  payload: object
): RequestOptionsFactory<any> {
  function __updateMarketingConsent({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFieldMaskToRESTFieldMask,
        paths: TRANSFORM_PATHS_1,
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: TRANSFORM_PATHS_0,
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.marketing.marketing_consent.v1.marketing_consent',
      method: 'PATCH' as any,
      methodFqn:
        'wix.marketing.v1.MarketingConsentService.UpdateMarketingConsent',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixMarketingV1MarketingConsentServiceUrl({
        protoPath: '/v1/marketing-consent/{marketingConsent.id}',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: TRANSFORM_PATHS_0,
          },
        ]),
    };

    return metadata;
  }

  return __updateMarketingConsent;
}

/** Deletes a marketing consent. */
export function deleteMarketingConsent(
  payload: object
): RequestOptionsFactory<any> {
  function __deleteMarketingConsent({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.marketing.marketing_consent.v1.marketing_consent',
      method: 'DELETE' as any,
      methodFqn:
        'wix.marketing.v1.MarketingConsentService.DeleteMarketingConsent',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixMarketingV1MarketingConsentServiceUrl({
        protoPath: '/v1/marketing-consent/{marketingConsentId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __deleteMarketingConsent;
}

/**
 * Removes a marketing consent. The consent is cancelled, and the `state` is updated to `REVOKED`.
 * The marketing consent entity still exists, but the recipient is no longer eligible to receive communication.
 * To delete a marketing consent entirely, use Delete Marketing Consent.
 *
 * Required fields:
 * - `details.type`.
 * - `details.email` OR `details.phone`.
 * - `info.lastRevokeActivity`.
 */
export function removeMarketingConsent(
  payload: object
): RequestOptionsFactory<any> {
  function __removeMarketingConsent({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: TRANSFORM_PATHS_2,
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.marketing.marketing_consent.v1.marketing_consent',
      method: 'POST' as any,
      methodFqn:
        'wix.marketing.v1.MarketingConsentService.RemoveMarketingConsent',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixMarketingV1MarketingConsentServiceUrl({
        protoPath: '/v1/marketing-consent/remove',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: TRANSFORM_PATHS_0,
          },
        ]),
    };

    return metadata;
  }

  return __removeMarketingConsent;
}

/**
 * Retrieves a list of marketing consents, given the provided paging, filtering, and sorting. Up to 100 marketing consents can be returned per request.
 *
 * The default `sort` is `id` in `ASC` order. For a detailed list of supported operations, see filtering and sorting for marketing consent properties. To learn how to query marketing consents, see API Query Language.
 */
export function queryMarketingConsent(
  payload: object
): RequestOptionsFactory<any> {
  function __queryMarketingConsent({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: TRANSFORM_PATHS_3,
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.marketing.marketing_consent.v1.marketing_consent',
      method: 'POST' as any,
      methodFqn:
        'wix.marketing.v1.MarketingConsentService.QueryMarketingConsent',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixMarketingV1MarketingConsentServiceUrl({
        protoPath: '/v1/marketing-consent/query',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: TRANSFORM_PATHS_0,
          },
        ]),
    };

    return metadata;
  }

  return __queryMarketingConsent;
}

/**
 * Creates or updates a marketing consent.
 * Required fields:
 * - `details.type`.
 * - `details.email` OR `details.phone`.
 * - `state`.
 *
 * When a marketing consent's `state` is `PENDING` or `CONFIRMED`, the `info.lastConfirmationActivity` field is required.
 * When a marketing consent's `state` is `REVOKED`, the `info.lastRevokeActivity` field is required.
 *
 * >**Note:** For existing marketing consents with `{"type": "EMAIL"}`, you can't update the `state` to `UNKNOWN_STATE`. Trying to do so maintains the current state. However, you can create a new marketing consent and set the `state` to `UNKNOWN_STATE`. Note that you can't create more than a single consent per email or phone number.
 */
export function upsertMarketingConsent(
  payload: object
): RequestOptionsFactory<any> {
  function __upsertMarketingConsent({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: TRANSFORM_PATHS_0,
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.marketing.marketing_consent.v1.marketing_consent',
      method: 'POST' as any,
      methodFqn:
        'wix.marketing.v1.MarketingConsentService.UpsertMarketingConsent',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixMarketingV1MarketingConsentServiceUrl({
        protoPath: '/v1/marketing-consent/upsert',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: TRANSFORM_PATHS_0,
          },
        ]),
    };

    return metadata;
  }

  return __upsertMarketingConsent;
}

/**
 * Creates or updates multiple marketing consents.
 * Required fields:
 * - `details.type`.
 * - `details.email` OR `details.phone`.
 * - `state`.
 *
 * When a marketing consent's `state` is `PENDING` or `CONFIRMED`, the `info.lastConfirmationActivity` field is required.
 * When a marketing consent's `state` is `REVOKED`, the `info.lastRevokeActivity` field is required.
 *
 * >**Note:** For existing marketing consents with `{"type": "EMAIL"}`, you can't update the `state` to `UNKNOWN_STATE`. Trying to do so maintains the current state. However, you can create a new marketing consent and set the `state` to `UNKNOWN_STATE`. Note that you can't create more than a single consent per email or phone number.
 */
export function bulkUpsertMarketingConsent(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkUpsertMarketingConsent({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: TRANSFORM_PATHS_4,
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.marketing.marketing_consent.v1.marketing_consent',
      method: 'POST' as any,
      methodFqn:
        'wix.marketing.v1.MarketingConsentService.BulkUpsertMarketingConsent',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixMarketingV1MarketingConsentServiceUrl({
        protoPath: '/v1/bulk/marketing-consent/upsert',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: TRANSFORM_PATHS_5,
          },
        ]),
    };

    return metadata;
  }

  return __bulkUpsertMarketingConsent;
}
